import { supabase } from '@/lib/supabase';

export type ChatReportReason = 'spam' | 'offensive' | 'scam' | 'harassment' | 'other';

export async function reportChatMessage(input: {
  agreementId: string;
  reportedUserId: string;
  reason: ChatReportReason;
  messageId?: string | null;
  note?: string | null;
}): Promise<void> {
  const { error } = await supabase.from('chat_reports').insert({
    agreement_id: input.agreementId,
    message_id: input.messageId ?? null,
    reported_user_id: input.reportedUserId,
    reporter_id: (await supabase.auth.getUser()).data.user!.id,
    reason: input.reason,
    note: input.note?.trim() || null,
  });
  if (error) throw error;
}

export async function blockUser(blockedId: string): Promise<void> {
  const { error } = await supabase.from('blocked_users').insert({
    blocker_id: (await supabase.auth.getUser()).data.user!.id,
    blocked_id: blockedId,
  });
  if (error) throw error;
}

export async function unblockUser(blockedId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  const { error } = await supabase
    .from('blocked_users')
    .delete()
    .eq('blocker_id', user.id)
    .eq('blocked_id', blockedId);
  if (error) throw error;
}

/** Όλες οι σχέσεις block που αφορούν τον τρέχοντα χρήστη (και οι δύο κατευθύνσεις) — το RLS περιορίζει ήδη το αποτέλεσμα σε αυτές. */
export async function fetchMyBlockRelations(): Promise<{ blocker_id: string; blocked_id: string }[]> {
  const { data, error } = await supabase.from('blocked_users').select('blocker_id, blocked_id');
  if (error) throw error;
  return (data ?? []) as { blocker_id: string; blocked_id: string }[];
}

export type ChatReportRow = {
  id: string;
  agreement_id: string;
  message_id: string | null;
  reported_user_id: string;
  reporter_id: string;
  reason: ChatReportReason;
  note: string | null;
  created_at: string;
  reviewed: boolean;
  reviewed_at: string | null;
  reviewed_by: string | null;
};

/** Μόνο για admin — το RLS (chat_reports_select_own_or_admin) περιορίζει ήδη το αποτέλεσμα. */
export async function fetchChatReports(filter: 'pending' | 'reviewed' | 'all'): Promise<ChatReportRow[]> {
  let q = supabase.from('chat_reports').select('*').order('created_at', { ascending: false });
  if (filter === 'pending') q = q.eq('reviewed', false);
  if (filter === 'reviewed') q = q.eq('reviewed', true);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as ChatReportRow[];
}

/** Το κείμενο του αναφερόμενου μηνύματος — ο admin δεν είναι μέρος της συμφωνίας,
 * οπότε το agreement_messages_select RLS δεν του δίνει πρόσβαση· χρησιμοποιεί
 * ξεχωριστό admin-only RPC. */
export async function fetchReportedMessageBody(messageId: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('admin_get_reported_message_body' as any, { p_message_id: messageId });
  if (error) throw error;
  return (data as string | null) ?? null;
}

export async function markChatReportReviewed(reportId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  const { error } = await supabase
    .from('chat_reports')
    .update({ reviewed: true, reviewed_at: new Date().toISOString(), reviewed_by: user?.id ?? null })
    .eq('id', reportId);
  if (error) throw error;
}

/** Όλα τα user_id των συμβαλλομένων μιας συμφωνίας (creator/counterparty + agreement_parties). */
export async function fetchAgreementPartyIds(agreementId: string): Promise<string[]> {
  const [{ data: agreement }, { data: parties }] = await Promise.all([
    supabase.from('agreements').select('creator_id, counterparty_id').eq('id', agreementId).maybeSingle(),
    supabase.from('agreement_parties').select('user_id').eq('agreement_id', agreementId),
  ]);
  const ids = new Set<string>();
  const a = agreement as { creator_id: string | null; counterparty_id: string | null } | null;
  if (a?.creator_id) ids.add(a.creator_id);
  if (a?.counterparty_id) ids.add(a.counterparty_id);
  for (const p of (parties ?? []) as { user_id: string | null }[]) if (p.user_id) ids.add(p.user_id);
  return [...ids];
}
